import React, { useState, useEffect } from "react";
import { firestore as db, firebaseApp } from "../firebase";
import * as fsdb from "firebase/firestore";
import styles from "../styles/UnitModifier.module.css";

type UnitList = Array<Unit>;
type Unit = { name: string; order: number; lessons: Lesson[]; id: string };
type Lesson = {
    type: string;
    order: number;
    title: string;
    id: string;
    url: string;
};

interface Lessons {
    [key: string]: Lesson;
}

interface Assignment {
    name: string;
    description: string;
    due: string;
    id: string;
}


const getUnits = async (unitType: string): Promise<UnitList> => {
    const unitsRef = fsdb.collection(db, unitType);
    const query = fsdb.query(unitsRef, fsdb.orderBy("order"), fsdb.limit(999));
    const units_raw = await fsdb.getDocs(query);
    return (
        units_raw?.docs.map((doc) => ({
            name: doc.data().name,
            order: doc.data().order,
            lessons: doc.data().lessons || [],
            id: doc.id,
        })) || []
    );
};

const UnitModifier: React.FC<{ unitType: string }> = ({ unitType }) => {
    const [units, setUnits] = useState<UnitList>([]);
    const [newUnitName, setNewUnitName] = useState("");
    const [newLesson, setNewLesson] = useState({ name: "", type: "", url: "" });
    const [selectedUnit, setSelectedUnit] = useState<string | null>(null);
    const [editingLesson, setEditingLesson] = useState<Lesson | null>(null);

    useEffect(() => {
        const fetchUnits = async () => {
            const fetchedUnits = await getUnits(unitType);
            setUnits(fetchedUnits);
        };
        fetchUnits();
    }, [unitType]);

    const addUnit = async () => {
        const newUnit = {
            name: newUnitName,
            order: units.length + 1,
            lessons: [],
        };
        const docRef = await fsdb.addDoc(fsdb.collection(db, unitType), newUnit);
        setUnits([...units, { ...newUnit, id: docRef.id }]);
        setNewUnitName("");
    };

    const addNewLesson = async (unitId: string) => {
        if (!newLesson.name || !newLesson.type || !newLesson.url) return;
        const unitToUpdate = units.find((unit) => unit.id === unitId);
        if (!unitToUpdate) return;
        const sortedLessons = [...unitToUpdate.lessons].sort((a, b) => a.order - b.order);
        const newOrder = sortedLessons.length + 1;
        const newLessonData: Lesson = {
            type: newLesson.type,
            order: newOrder,
            title: newLesson.name,
            id: `${unitToUpdate.order}_${newOrder}`,
            url: newLesson.url,
        };
        await fsdb.updateDoc(
            fsdb.doc(db, unitType, unitId),
            {
                lessons: [...unitToUpdate.lessons, newLessonData],
            }
        );
        setUnits((prevUnits) =>
            prevUnits.map((unit) =>
                unit.id === unitId
                    ? { ...unit, lessons: [...unit.lessons, newLessonData] }
                    : unit
            )
        );
        clearModalState();
    };

    const reorderLesson = async (
        unitId: string,
        lessonId: string,
        direction: "up" | "down"
    ) => {
        const unitToUpdate = units.find((unit) => unit.id === unitId);
        if (!unitToUpdate) return;
        const sortedLessons = [...unitToUpdate.lessons].sort((a, b) => a.order - b.order);
        const currentIndex = sortedLessons.findIndex((lesson) => lesson.id === lessonId);
        if (currentIndex === -1) return;
        const targetIndex = direction === "up" ? currentIndex - 1 : currentIndex + 1;
        if (targetIndex < 0 || targetIndex >= sortedLessons.length) return;
        const currentLesson = sortedLessons[currentIndex];
        const targetLesson = sortedLessons[targetIndex];
        const tempOrder = currentLesson.order;
        currentLesson.order = targetLesson.order;
        targetLesson.order = tempOrder;
        currentLesson.id = `${unitToUpdate.order}_${currentLesson.order}`;
        targetLesson.id = `${unitToUpdate.order}_${targetLesson.order}`;
        try {
            await fsdb.updateDoc(
                fsdb.doc(db, unitType, unitId),
                {
                    lessons: sortedLessons,
                }
            );
            setUnits((prevUnits) =>
                prevUnits.map((unit) =>
                    unit.id === unitId
                        ? { ...unit, lessons: sortedLessons }
                        : unit
                )
            );
        } catch (error) {
            console.error("Error reordering lessons:", error);
        }
    };

    const clearModalState = () => {
        setNewLesson({ name: "", type: "", url: "" });
        setSelectedUnit(null);
        setEditingLesson(null);
    };

    const handleEditLesson = (lesson: Lesson) => {
        setEditingLesson(lesson);
        setSelectedUnit(
            units.find((unit) => unit.lessons.some((l) => l.id === lesson.id))?.id || null
        );
        setNewLesson({
            name: lesson.title,
            type: lesson.type,
            url: lesson.url,
        });
    };

    const updateLesson = async () => {
        if (!editingLesson || !selectedUnit) return;

        const unitToUpdate = units.find((unit) => unit.id === selectedUnit);
        if (!unitToUpdate) return;

        const updatedLesson: Lesson = {
            ...editingLesson,
            title: newLesson.name,
            type: newLesson.type,
            url: newLesson.url,
        };

        try {
            await fsdb.updateDoc(
                fsdb.doc(
                db,
                unitType,
                    selectedUnit
                ),
                {
                    lessons: unitToUpdate.lessons.map((lesson) =>
                        lesson.id === editingLesson.id ? updatedLesson : lesson
                    ),
                }
            );

            setUnits((prevUnits) =>
                prevUnits.map((unit) =>
                    unit.id === selectedUnit
                        ? {
                              ...unit,
                              lessons: unit.lessons.map((lesson) =>
                                  lesson.id === editingLesson.id
                                      ? updatedLesson
                                      : lesson
                              ),
                          }
                        : unit
                )
            );
            clearModalState();
        } catch (error) {
            console.error("Error updating lesson:", error);
        }
    };

    const deleteLesson = async () => {
        if (!editingLesson || !selectedUnit) return;
    
        const unitToUpdate = units.find((unit) => unit.id === selectedUnit);
        if (!unitToUpdate) return;
    
        // Remove the lesson and sort remaining lessons
        const remainingLessons = unitToUpdate.lessons
            .filter((lesson) => lesson.id !== editingLesson.id)
            .sort((a, b) => a.order - b.order);
    
        // Update orders and IDs for remaining lessons
        const updatedLessons = remainingLessons.map((lesson, index) => ({
            ...lesson,
            order: index + 1,
            id: `${unitToUpdate.order}_${index + 1}`
        }));
    
        try {
            await fsdb.updateDoc(
                fsdb.doc(
                db,
                unitType,
                    selectedUnit
                ),
                { lessons: updatedLessons }
            );
    
            setUnits(
                units.map((unit) =>
                    unit.id === selectedUnit
                        ? {
                            ...unit,
                            lessons: updatedLessons,
                        }
                        : unit
                )
            );
            clearModalState();
        } catch (error) {
            console.error("Error deleting lesson:", error);
        }
    };

    const deleteUnit = async (unitId: string) => {
        const randomWords = ["apple", "banana", "cherry", "date", "elderberry"];
        const theRandom =
            randomWords[Math.floor(Math.random() * randomWords.length)];
        const deleteUnitConfirmation: string | null = prompt(
            `Type the word ${theRandom} to confirm deletion.`
        );
        if (theRandom === null) return;
        if (!theRandom || theRandom !== deleteUnitConfirmation) {
            alert("Please type a correct word to confirm deletion.");
            return;
        }

        try {
            await fsdb.deleteDoc(
                fsdb.doc(
                db,
                unitType,
                    unitId
                )
            );
            setUnits(units.filter((unit) => unit.id !== unitId));
        } catch (error) {
            console.error("Error deleting unit:", error);
        }
    };

    return (
        <div className={styles.unitModifier}>
            {units
                .sort((a, b) => a.order - b.order)
                .map((unit) => (
                    <div key={unit.id}>
                        <details className={styles.listing}>
                            <summary>
                                <h4>
                                    {unit.name}
                                    <span
                                        className="material-icons"
                                        style={{
                                            color: "#E57373",
                                            cursor: "pointer",
                                            marginLeft: "10px",
                                        }}
                                        onClick={() => deleteUnit(unit.id)}
                                    >
                                        delete
                                    </span>
                                </h4>
                            </summary>
                            <ul className={styles.listings}>
                                {unit.lessons
                                    .sort((a, b) => a.order - b.order)
                                    .map((lesson, index, sortedLessons) => (
                                        <li key={lesson.id}>
                                            <div className={styles.lessonRow}>
                                                <span
                                                    className="material-icons"
                                                    onClick={(e) => {
                                                        if (index > 0) {
                                                            e.stopPropagation();
                                                            reorderLesson(
                                                                unit.id,
                                                                lesson.id,
                                                                "up"
                                                            );
                                                        }
                                                    }}
                                                    style={{
                                                        cursor:
                                                            index > 0
                                                                ? "pointer"
                                                                : "default",
                                                        color:
                                                            index > 0
                                                                ? "inherit"
                                                                : "#ccc",
                                                    }}
                                                >
                                                    arrow_upward
                                                </span>
                                                <span
                                                    className="material-icons"
                                                    onClick={(e) => {
                                                        if (
                                                            index <
                                                            sortedLessons.length -
                                                                1
                                                        ) {
                                                            e.stopPropagation();
                                                            reorderLesson(
                                                                unit.id,
                                                                lesson.id,
                                                                "down"
                                                            );
                                                        }
                                                    }}
                                                    style={{
                                                        cursor:
                                                            index <
                                                            sortedLessons.length -
                                                                1
                                                                ? "pointer"
                                                                : "default",
                                                        color:
                                                            index <
                                                            sortedLessons.length -
                                                                1
                                                                ? "inherit"
                                                                : "#ccc",
                                                    }}
                                                >
                                                    arrow_downward
                                                </span>
                                                <a target="_blank" href={lesson.url}><span>{lesson.title}</span></a>
                                                <span
                                                    className="material-icons edit-icon"
                                                    onClick={() =>
                                                        handleEditLesson(lesson)
                                                    }
                                                    style={{
                                                        cursor: "pointer",
                                                    }}
                                                >
                                                    edit
                                                </span>
                                            </div>
                                        </li>
                                    ))}
                            </ul>
                            <button
                                className={styles.addLessonBtn}
                                onClick={() => {
                                    setSelectedUnit(unit.id);
                                    setEditingLesson(null);
                                }}
                            >
                                <span className="material-icons">
                                    add_circle
                                </span>{" "}
                                Add Lesson
                            </button>
                        </details>
                    </div>
                ))}
            <div>
                <input
                    type="text"
                    placeholder="Unit Name"
                    value={newUnitName}
                    className={styles.addUnitInput}
                    onChange={(e) => setNewUnitName(e.target.value)}
                />
                <button className={styles.addUnitBtn} onClick={addUnit}>
                    Add Unit
                </button>
            </div>

            {/* Modal */}
            {(selectedUnit || editingLesson) && (
                <div className={styles.modal} style={{ display: "flex" }}>
                    <div className={styles.modalContent}>
                        <label>Lesson Name</label>
                        <input
                            type="text"
                            placeholder="Lesson Name"
                            value={newLesson.name}
                            onChange={(e) =>
                                setNewLesson({
                                    ...newLesson,
                                    name: e.target.value,
                                })
                            }
                        />
                        <label>Lesson Type</label>
                        <select
                            value={newLesson.type}
                            onChange={(e) =>
                                setNewLesson({
                                    ...newLesson,
                                    type: e.target.value,
                                })
                            }
                        >
                            <option value="">Select type</option>
                            <option value="file">File</option>
                            <option value="lecture">Lecture</option>
                        </select>
                        <label>Lesson URL</label>
                        <input
                            type="url"
                            placeholder="Lesson URL"
                            value={newLesson.url}
                            onChange={(e) =>
                                setNewLesson({
                                    ...newLesson,
                                    url: e.target.value,
                                })
                            }
                        />
                        <div className={styles.modalButtons}>
                            <button
                                onClick={() =>
                                    editingLesson
                                        ? updateLesson()
                                        : addNewLesson(selectedUnit!)
                                }
                            >
                                {editingLesson ? "Update Lesson" : "Add Lesson"}
                            </button>
                            <button
                                className={styles.cancelBtn}
                                onClick={clearModalState}
                            >
                                Cancel
                            </button>
                            {editingLesson && (
                                <button
                                    className={styles.deleteBtn}
                                    onClick={deleteLesson}
                                >
                                    Delete Lesson
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default UnitModifier;
